'use client';

import { Button, Input, message } from 'antd';
import { useEffect, useState } from 'react';

import Loading from '@/components/loading';
import TitleDescription from '@/components/title-description';
import useAvatarCropper from '@/hooks/useAvatarCropper';
import { useModel } from '@/hooks/useModel';
import { useTranslation } from '@/i18n/client';

import DeleteAccount from './delete-account';
import ExportMemories from './export-memory';
import styles from './index.less';

export default function UserProfile(): JSX.Element {
  const { userProfile, updateUserProfile, resetPassword } = useModel('auth-model', (model) => ({
    userProfile: model.userProfile,
    updateUserProfile: model.updateUserProfile,
    resetPassword: model.resetPassword
  }));

  const { t } = useTranslation();

  const [username, setUsername] = useState<string>(userProfile?.name ?? '');
  const [avatar, setAvatar] = useState<string>(userProfile?.avatar ?? '');
  const [messageApi, messageContext] = message.useMessage();
  const [isResetPwd, setIsResetPwd] = useState<boolean>(false);
  const { FileInput, CropperModal, isUploading } = useAvatarCropper({
    handleSetAvatarCb: (_avatar) => {
      setAvatar(_avatar);
      updateProfile({
        avatar: _avatar
      });
    },
    cropperModalTitle: 'Avatar'
  });

  useEffect(() => {
    setAvatar(userProfile?.avatar ?? '');
  }, [userProfile?.avatar]);

  useEffect(() => {
    setUsername(userProfile?.name ?? '');
  }, [userProfile?.name]);

  const updateProfile = (data: { username?: string; avatar?: string }) => {
    const { username: newUserName, avatar: newAvatar } = data;

    updateUserProfile({
      name: newUserName,
      avatar: newAvatar
    })
      .then(() => {
        messageApi.success('Changes saved');
      })
      .catch((error) => {
        messageApi.error(error?.message ?? 'Update failed');
      });
  };

  const clickResetPassword = () => {
    setIsResetPwd(true);
    resetPassword()
      .then(() => {
        messageApi.success(t('menu.accountSettingsModal.passwordTips'));
      })
      .catch((error) => {
        messageApi.error(error?.message ?? 'Reset password failed');
      })
      .finally(() => {
        setIsResetPwd(false);
      });
  };

  return (
    <div className={styles.container}>
      <div className={styles.bodyContainer}>
        <TitleDescription
          className={styles.titleItem}
          title={t('menu.accountSettingsModal.email')}
        />
        <Input className={styles.input} disabled={true} value={userProfile?.email} />
        <TitleDescription
          className={styles.titleItem}
          desc={t('menu.accountSettingsModal.nameDescription')}
          title={t('menu.accountSettingsModal.name')}
        />
        <Input
          className={styles.input}
          onBlur={(event) => {
            if (event.target.value === userProfile?.name) {
              return;
            }

            updateProfile({
              username: event.target.value
            });
          }}
          onChange={(event) => {
            setUsername(event.target.value);
          }}
          value={username}
        />
        <TitleDescription
          className={styles.titleItem}
          desc={t('menu.accountSettingsModal.avatarDescription')}
          title={t('menu.accountSettingsModal.avatar')}
        />
        <div className={styles.uploadAvatar}>
          <label>
            {FileInput}
            <img className={styles.avatarImg} src={avatar} />
          </label>
          {isUploading && (
            <div className={styles.maskView}>
              <Loading />
            </div>
          )}

          {CropperModal}
        </div>

        <ExportMemories />

        <TitleDescription
          className={styles.titleItem}
          title={t('menu.accountSettingsModal.password')}
        />
        <Button
          className={styles.reset}
          loading={isResetPwd}
          onClick={() => clickResetPassword()}
          type="primary"
        >
          {t('menu.accountSettingsModal.passwordBtn')}
        </Button>

        <DeleteAccount />
      </div>

      {messageContext}
    </div>
  );
}
